import { useMemo } from "react";
import { Empty, Skeleton, Tag, Tooltip, theme } from "antd";
import { TbLock, TbWorld } from "react-icons/tb";
import { LifeCycle, StageDto } from "../../dtos/stage.dto";
import { LIFECYCLE_META, LIFECYCLE_ORDER, resolveStageColor, resolveStageIcon } from "./constants";

interface StageListProps {
  stages: StageDto[];
  loading: boolean;
  selectedId: string | null;
  emptyHint: string;
  onSelect: (stage: StageDto) => void;
}

const StageList = ({ stages, loading, selectedId, emptyHint, onSelect }: StageListProps) => {
  const { token } = theme.useToken();

  const grouped = useMemo(() => {
    const buckets = new Map<LifeCycle, StageDto[]>(LIFECYCLE_ORDER.map((lifecycle) => [lifecycle, []]));
    stages.forEach((stage) => {
      buckets.get(stage.lifecycle)?.push(stage);
    });
    return LIFECYCLE_ORDER.map((lifecycle) => ({
      lifecycle,
      items: (buckets.get(lifecycle) ?? []).slice().sort((a, b) => a.label.localeCompare(b.label)),
    })).filter((group) => group.items.length > 0);
  }, [stages]);

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton active paragraph={{ rows: 6 }} title={false} />
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div style={{ padding: "48px 16px" }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ color: token.colorTextTertiary }}>{emptyHint}</span>}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 8px 16px" }}>
      {grouped.map((group) => {
        const meta = LIFECYCLE_META[group.lifecycle];
        return (
          <section key={group.lifecycle} style={{ marginTop: 12 }}>
            <header
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "0 8px 6px",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: token.colorTextTertiary,
              }}
            >
              <meta.Icon size={14} />
              {meta.label}
              <span
                style={{
                  marginLeft: "auto",
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: 0,
                }}
              >
                {group.items.length}
              </span>
            </header>

            {group.items.map((stage) => {
              const swatch = resolveStageColor(stage.color);
              const Icon = resolveStageIcon(stage.icon);
              const active = stage._id === selectedId;

              return (
                <button
                  key={stage._id}
                  type="button"
                  aria-current={active}
                  onClick={() => onSelect(stage)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: 8,
                    marginBottom: 2,
                    border: "none",
                    borderRadius: 10,
                    textAlign: "left",
                    cursor: "pointer",
                    opacity: stage.archived ? 0.55 : 1,
                    background: active ? token.colorPrimaryBg : "transparent",
                    boxShadow: active ? `inset 0 0 0 1px ${token.colorPrimaryBorder}` : "none",
                    transitionProperty: "background-color, box-shadow",
                    transitionDuration: "0.15s",
                    transitionTimingFunction: "cubic-bezier(0.2, 0, 0, 1)",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      borderRadius: 8,
                      background: swatch.bg,
                      color: swatch.text,
                      boxShadow: `inset 0 0 0 1px ${swatch.border}`,
                    }}
                  >
                    <Icon size={17} />
                  </span>

                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 500,
                        color: token.colorText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {stage.label}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: 11,
                        fontFamily: token.fontFamilyCode,
                        color: token.colorTextQuaternary,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {stage.value}
                    </span>
                  </span>

                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      flexShrink: 0,
                      color: token.colorTextQuaternary,
                    }}
                  >
                    {stage.archived ? (
                      <Tag bordered={false} style={{ marginInlineEnd: 0, fontSize: 11 }}>
                        Archived
                      </Tag>
                    ) : null}
                    {stage.public ? (
                      <Tooltip title="Visible to other organisations">
                        <span style={{ display: "inline-flex" }}>
                          <TbWorld size={14} />
                        </span>
                      </Tooltip>
                    ) : null}
                    {stage.locked ? (
                      <Tooltip title="System stage">
                        <span style={{ display: "inline-flex" }}>
                          <TbLock size={14} />
                        </span>
                      </Tooltip>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </section>
        );
      })}
    </div>
  );
};

export default StageList;
